import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Loader2, ShieldCheck, FileText, ExternalLink } from "lucide-react";
import { billingApi } from "@/lib/api-client";
import { toast } from "sonner";

const TIER_ACCENTS = {
  command: "border-sky-500/40 text-sky-300",
  director: "border-emerald-500/40 text-emerald-300",
  sovereign: "border-amber-500/40 text-amber-400",
  patriarch: "border-rose-500/40 text-rose-300",
};

export default function BillingCommand() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    billingApi.tiers().then(setData).catch(() => toast.error("Failed to load tiers"));
    billingApi.invoices().then((r) => setInvoices(r.invoices || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (tier) => {
    setBusy(tier);
    try {
      const r = await billingApi.checkout(tier, window.location.origin);
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await billingApi.portal(window.location.origin);
      window.location.href = r.portal_url;
    } catch (e) {
      toast.error(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail : "Billing portal unavailable");
      setBusy("");
    }
  };

  if (!data) return (
    <div className="min-h-screen uc-grain flex items-center justify-center">
      <Loader2 className="animate-spin text-amber-400" />
    </div>
  );

  return (
    <div className="min-h-screen uc-grain relative" data-testid="billing-command-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="uc-chip flex items-center gap-1.5 hover:bg-white/5" data-testid="billing-back-btn">
              <ArrowLeft size={11} /> Dashboard
            </Link>
            <div>
              <div className="font-mono-uc text-[9px] uppercase tracking-[0.3em] text-amber-400/80">SOVEREIGN BILLING</div>
              <h1 className="text-2xl font-semibold text-white">Directorate Tiers</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`uc-chip font-mono-uc text-[10px] uppercase ${data.mode === "live" ? "text-emerald-300 border-emerald-500/50" : "text-amber-400 border-amber-500/40"}`}
              data-testid="billing-mode-badge">
              {data.mode === "live" ? "LIVE MODE" : "TEST MODE — goes live when the Stripe account is claimed"}
            </span>
          </div>
        </div>

        {data.current_tier && (
          <div className="mb-6 p-4 border border-emerald-500/30 bg-emerald-500/5 flex flex-wrap items-center justify-between gap-3" data-testid="current-subscription-bar">
            <div className="flex items-center gap-3">
              <ShieldCheck size={16} className="text-emerald-300" />
              <div>
                <div className="text-sm text-white">
                  Active: <span className="text-emerald-300 font-semibold uppercase">{data.current_tier}</span>
                  <span className="text-white/40 font-mono-uc text-[10px] ml-2 uppercase">status: {data.billing_status}</span>
                </div>
                <div className="font-mono-uc text-[10px] text-white/50 uppercase" data-testid="mandate-usage">
                  Mandates: {data.active_mandates} / {data.mandate_cap ?? "∞"} active
                </div>
              </div>
            </div>
            <button onClick={openPortal} disabled={busy === "portal"}
              className="uc-chip hover:bg-amber-500/10 hover:border-amber-500/50 text-amber-400 flex items-center gap-1.5"
              data-testid="billing-portal-btn">
              {busy === "portal" ? <Loader2 size={11} className="animate-spin" /> : <ExternalLink size={11} />}
              Manage Subscription · Invoices
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.tiers.map((t) => (
            <div key={t.id} className={`border bg-black/40 p-5 flex flex-col ${TIER_ACCENTS[t.id] || "border-white/10"}`}
              data-testid={`tier-card-${t.id}`}>
              <div className="flex items-center gap-2 mb-2">
                {t.white_glove && <Crown size={13} className="text-rose-300" />}
                <div className="font-mono-uc text-[10px] uppercase tracking-widest">{t.name}</div>
              </div>
              <div className="text-3xl font-semibold text-white mb-1">
                ${t.monthly_usd.toLocaleString()}<span className="text-sm text-white/40">/mo</span>
              </div>
              <ul className="text-xs text-white/60 space-y-1.5 my-4 flex-1">
                <li>• {t.mandate_cap} concurrent active agent mandates</li>
                <li>• Proof-of-Work artifact on every close</li>
                <li>• Evidence Vault access</li>
                {t.white_glove && <li className="text-rose-300">• White-glove onboarding</li>}
              </ul>
              <button
                onClick={() => subscribe(t.id)}
                disabled={!!busy || data.current_tier === t.id}
                className="uc-chip w-full justify-center py-2 hover:bg-white/10 disabled:opacity-40"
                data-testid={`subscribe-btn-${t.id}`}>
                {busy === t.id ? <Loader2 size={12} className="animate-spin" />
                  : data.current_tier === t.id ? "CURRENT TIER" : "SUBSCRIBE"}
              </button>
            </div>
          ))}
        </div>

        {invoices.length > 0 && (
          <div className="mt-8 border border-white/10 bg-black/30 p-4" data-testid="invoice-list">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
              <FileText size={12} /> Invoices
            </div>
            {invoices.map((i) => (
              <div key={i.id} className="flex items-center justify-between py-2 border-b border-white/5 text-xs">
                <span className="font-mono-uc text-white/60">{i.id}</span>
                <span className={i.status === "paid" ? "text-emerald-300" : "text-amber-400"}>{i.status}</span>
                <span className="text-white">${i.amount_paid.toLocaleString()}</span>
                {i.invoice_pdf && (
                  <a href={i.invoice_pdf} className="text-amber-400 hover:underline" data-testid={`invoice-pdf-${i.id}`}>
                    Receipt PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={() => navigate("/evidence")} className="uc-chip hover:bg-white/5" data-testid="goto-evidence-btn">
            Evidence Vault →
          </button>
        </div>
      </div>
    </div>
  );
}
